
import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils/cn";
import { formatCount } from "@/lib/utils/format";
import { useToast } from "@/components/ui/ToastProvider";
import type { VoteType } from "@/types";

interface VoteButtonsProps {
  postId: string;
  userId: string | null;
  currentVote: VoteType | null;
  score: number;
  onVoteChange: (newVote: VoteType | null, scoreDelta: number) => void;
  size?: "sm" | "md";
  orientation?: "vertical" | "horizontal";
}

export function VoteButtons({
  postId,
  userId,
  currentVote,
  score,
  onVoteChange,
  size = "md",
  orientation = "vertical",
}: VoteButtonsProps) {
  const [isVoting, setIsVoting] = useState(false);
  const { info } = useToast();

  const handleVote = useCallback(
    async (voteType: VoteType) => {
      if (!userId) {
        info("Sign in to vote", "Create an account to engage with posts.");
        return;
      }

      if (isVoting) return;

      // Optimistic update
      let scoreDelta = 0;
      let newVote: VoteType | null = null;

      if (currentVote === voteType) {
        // Remove vote
        scoreDelta = voteType === "up" ? -1 : 1;
        newVote = null;
      } else if (currentVote === null) {
        // New vote
        scoreDelta = voteType === "up" ? 1 : -1;
        newVote = voteType;
      } else {
        // Change vote (from up to down or vice versa)
        scoreDelta = voteType === "up" ? 2 : -2;
        newVote = voteType;
      }

      onVoteChange(newVote, scoreDelta);
      setIsVoting(true);

      try {
        const res = await fetch(`/api/posts/${postId}/vote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ vote_type: voteType }),
        });

        if (!res.ok) {
          // Revert optimistic update
          onVoteChange(currentVote, -scoreDelta);
          throw new Error("Vote failed");
        }
      } catch {
        // Revert already done above
      } finally {
        setIsVoting(false);
      }
    },
    [postId, userId, currentVote, onVoteChange, isVoting, info]
  );

  const btnSize = size === "sm" ? "w-7 h-7" : "w-8 h-8";
  const iconSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  const scoreSize = size === "sm" ? "text-xs" : "text-sm";

  return (
    <div
      className={cn(
        "flex items-center gap-0.5",
        orientation === "vertical" ? "flex-col" : "flex-row"
      )}
    >
      {/* Upvote */}
      <motion.button
        onClick={() => handleVote("up")}
        whileTap={{ scale: 0.85 }}
        className={cn(
          "inline-flex items-center justify-center rounded-lg transition-all duration-150",
          btnSize,
          currentVote === "up"
            ? "text-brand bg-brand-muted"
            : "text-text-subtle hover:text-brand hover:bg-brand-muted"
        )}
        aria-label="Upvote"
        aria-pressed={currentVote === "up"}
        disabled={isVoting}
      >
        <svg
          className={iconSize}
          fill={currentVote === "up" ? "currentColor" : "none"}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </motion.button>

      {/* Score */}
      <div
        className={cn(
          "font-bold tabular-nums select-none transition-colors duration-150",
          scoreSize,
          currentVote === "up"
            ? "text-brand"
            : currentVote === "down"
            ? "text-destructive"
            : "text-text-muted"
        )}
      >
        <AnimatePresence mode="popLayout">
          <motion.span
            key={score}
            initial={{ opacity: 0, y: currentVote === "up" ? -8 : 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {formatCount(score)}
          </motion.span>
        </AnimatePresence>
      </div>

      {/* Downvote */}
      <motion.button
        onClick={() => handleVote("down")}
        whileTap={{ scale: 0.85 }}
        className={cn(
          "inline-flex items-center justify-center rounded-lg transition-all duration-150",
          btnSize,
          currentVote === "down"
            ? "text-destructive bg-destructive/10"
            : "text-text-subtle hover:text-destructive hover:bg-destructive/10"
        )}
        aria-label="Downvote"
        aria-pressed={currentVote === "down"}
        disabled={isVoting}
      >
        <svg
          className={iconSize}
          fill={currentVote === "down" ? "currentColor" : "none"}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </motion.button>
    </div>
  );
}
